"use client";

import { useState, useCallback, useEffect } from "react";
import { ArrowLeft, Palette } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Label } from "@/shadcn-components/ui/label";
import { Separator } from "@/shadcn-components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shadcn-components/ui/tabs";
import { Slider } from "@/shadcn-components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import { useChartSettingsStore } from "@/lib/chart-settings";
import { useSetScheme } from "@/lib/chart-settings/hooks";
import type { ChartStyle } from "@/lib/chart-settings/types";

interface ColorsContentProps {
  onBack: () => void;
}

interface ColorItem {
  id: keyof Omit<ChartStyle, "scheme">;
  label: string;
  color: string;
  category: "chart" | "price" | "trading";
}

export function ColorsContent({ onBack }: ColorsContentProps) {
  const { chartSettings, updateChartSettingsStyle } = useChartSettingsStore();
  const setScheme = useSetScheme();
  const { chartStyle } = chartSettings;
  
  const [selectedColorItem, setSelectedColorItem] = useState<ColorItem | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Available color schemes
  const SCHEMES = [
    { value: "blackOnWhite", label: "Black on White" },
    { value: "colorOnWhite", label: "Color on White" },
    { value: "greenOnBlack", label: "Green on Black" },
    { value: "custom", label: "Custom" },
  ];

  // Helper functions for color conversion
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const rgbToHex = (r: number, g: number, b: number) => {
    return "#" + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    }).join("");
  };

  const handleColorClick = (item: ColorItem) => {
    setSelectedColorItem(item);
    setShowColorPicker(true);
  };

  const handleColorChange = (color: string) => {
    if (selectedColorItem) {
      updateChartSettingsStyle(selectedColorItem.id, color);
      // When user changes a color manually, set scheme to custom
      updateChartSettingsStyle("scheme", "custom");
    }
  };

  const handleSchemeChange = (scheme: string) => {
    // Apply preset colors based on scheme
    if (scheme === "blackOnWhite" || scheme === "greenOnBlack" || scheme === "colorOnWhite") {
      setScheme(scheme);
    } else if (scheme === "custom") {
      // For custom, just update the scheme field without changing colors
      updateChartSettingsStyle("scheme", "custom");
    }
  };

  const COLOR_ITEMS: ColorItem[] = [
    { id: "foreground", label: "Foreground", color: chartStyle.foreground, category: "chart" },
    { id: "grid", label: "Grid", color: chartStyle.grid, category: "chart" },
    { id: "barUp", label: "Bar Up", color: chartStyle.barUp, category: "chart" },
    { id: "barDown", label: "Bar Down", color: chartStyle.barDown, category: "chart" },
    { id: "bullCandle", label: "Bull Candle", color: chartStyle.bullCandle, category: "chart" },
    { id: "bearCandle", label: "Bear Candle", color: chartStyle.bearCandle, category: "chart" },
    { id: "lineChart", label: "Line Chart", color: chartStyle.lineChart, category: "chart" },
    { id: "volumes", label: "Volumes", color: chartStyle.volumes, category: "chart" },
    { id: "bidPriceLine", label: "Bid Price Line", color: chartStyle.bidPriceLine, category: "price" },
    { id: "askPriceLine", label: "Ask Price Line", color: chartStyle.askPriceLine, category: "price" },
    { id: "stopsLevels", label: "Stops Levels", color: chartStyle.stopsLevels, category: "trading" },
  ];

  // Group colors by category
  const colorsByCategory = {
    chart: COLOR_ITEMS.filter(item => item.category === "chart"),
    price: COLOR_ITEMS.filter(item => item.category === "price"),
    trading: COLOR_ITEMS.filter(item => item.category === "trading"),
  };

  const ColorItemComponent = ({ item, index, total }: { item: ColorItem; index: number; total: number }) => (
    <Button
      variant="ghost"
      onClick={() => handleColorClick(item)}
      className={`w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none
        ${index === 0 ? "rounded-t-lg" : ""}
        ${index === total - 1 ? "rounded-b-lg" : ""}
      `}
    >
      <Label className="text-sm font-medium cursor-pointer">{item.label}</Label>
      <div
        className="w-6 h-6 rounded-full border border-border flex-shrink-0"
        style={{ backgroundColor: item.color }}
      />
    </Button>
  );

  // RGB Sliders Component
  const RGBSliders = ({ selectedColor, onColorChange }: { selectedColor: string; onColorChange: (color: string) => void }) => {
    const [r, setR] = useState(0);
    const [g, setG] = useState(0);
    const [b, setB] = useState(0);

    // Update local state when selectedColor changes externally
    useEffect(() => {
      const rgb = hexToRgb(selectedColor);
      setR(rgb.r);
      setG(rgb.g);
      setB(rgb.b);
    }, [selectedColor]);

    const updateColor = useCallback((newR: number, newG: number, newB: number) => {
      const hexColor = rgbToHex(newR, newG, newB);
      onColorChange(hexColor);
    }, [onColorChange]);

    const handleRChange = (value: number[]) => {
      const newR = value[0];
      setR(newR);
      updateColor(newR, g, b);
    };

    const handleGChange = (value: number[]) => {
      const newG = value[0];
      setG(newG);
      updateColor(r, newG, b);
    };

    const handleBChange = (value: number[]) => {
      const newB = value[0];
      setB(newB);
      updateColor(r, g, newB);
    };

    return (
      <div className="space-y-6">
        {/* RED Slider */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">RED</Label>
            <span className="text-sm font-mono text-muted-foreground">{r}</span>
          </div>
          <Slider
            value={[r]}
            onValueChange={handleRChange}
            max={255}
            step={1}
            className="w-full"
          />
        </div>

        {/* GREEN Slider */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">GREEN</Label>
            <span className="text-sm font-mono text-muted-foreground">{g}</span>
          </div>
          <Slider
            value={[g]}
            onValueChange={handleGChange}
            max={255}
            step={1}
            className="w-full"
          />
        </div>

        {/* BLUE Slider */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">BLUE</Label>
            <span className="text-sm font-mono text-muted-foreground">{b}</span>
          </div>
          <Slider
            value={[b]}
            onValueChange={handleBChange}
            max={255}
            step={1}
            className="w-full"
          />
        </div>

        {/* Color Preview */}
        <div className="mt-6 p-4 bg-muted/20 rounded-lg">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-lg border border-border"
              style={{ backgroundColor: selectedColor }}
            />
            <div>
              <p className="text-sm font-medium">Display P3 Hex Color #</p>
              <p className="text-lg font-mono">{selectedColor.replace('#', '').toUpperCase()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (showColorPicker && selectedColorItem) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowColorPicker(false)}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                <Palette className="h-5 w-5 text-blue-600" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">{selectedColorItem.label}</h2>
                <p className="text-sm text-muted-foreground">
                  Choose a color for {selectedColorItem.label?.toLowerCase()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Color Picker Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Color Picker Tabs */}
            <Tabs defaultValue="grid" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="grid" className="text-sm">Grid</TabsTrigger>
                <TabsTrigger value="sliders" className="text-sm">Sliders</TabsTrigger>
              </TabsList>
              
              <TabsContent value="grid" className="mt-0">
                <div className="space-y-4">
                  <div className="grid grid-cols-6 gap-3">
                    {[
                      "#F56565", "#3B82F6", "#10B981", "#EAB308", "#EF4444", "#8B5CF6",
                      "#06B6D4", "#F97316", "#EC4899", "#84CC16", "#6366F1", "#F59E0B",
                      "#000000", "#374151", "#6B7280", "#9CA3AF", "#D1D5DB", "#FFFFFF"
                    ].map((color) => (
                      <Button
                        key={color}
                        variant="outline"
                        className="w-12 h-12 p-0 border-2 hover:border-primary"
                        style={{ backgroundColor: color }}
                        onClick={() => handleColorChange(color)}
                      />
                    ))}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="sliders" className="mt-0">
                <RGBSliders 
                  selectedColor={selectedColorItem.color}
                  onColorChange={handleColorChange}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
              <Palette className="h-5 w-5 text-purple-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Colors</h2>
              <p className="text-sm text-muted-foreground">
                Customize chart appearance
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-6">
          {/* Scheme Section */}
          <div>
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
              COLOR SCHEME
            </h3>
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Scheme</Label>
                  <Select value={chartStyle.scheme} onValueChange={handleSchemeChange}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select scheme" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEMES.map((scheme) => (
                        <SelectItem key={scheme.value} value={scheme.value}>
                          {scheme.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colors Section with Tabs */}
          <div>
            <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
              CUSTOM COLORS
            </h3>
            
            <Tabs defaultValue="chart" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-3">
                <TabsTrigger value="chart" className="text-sm">Chart</TabsTrigger>
                <TabsTrigger value="price" className="text-sm">Price</TabsTrigger>
                <TabsTrigger value="trading" className="text-sm">Trading</TabsTrigger>
              </TabsList>
              
              <TabsContent value="chart" className="mt-0">
                <Card>
                  <CardContent className="p-0">
                    {colorsByCategory.chart.map((item, index) => (
                      <div key={item.id}>
                        <ColorItemComponent 
                          item={item} 
                          index={index} 
                          total={colorsByCategory.chart.length} 
                        />
                        {index < colorsByCategory.chart.length - 1 && <Separator />}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="price" className="mt-0">
                <Card>
                  <CardContent className="p-0">
                    {colorsByCategory.price.map((item, index) => (
                      <div key={item.id}>
                        <ColorItemComponent 
                          item={item} 
                          index={index} 
                          total={colorsByCategory.price.length} 
                        />
                        {index < colorsByCategory.price.length - 1 && <Separator />}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="trading" className="mt-0">
                <Card>
                  <CardContent className="p-0">
                    {colorsByCategory.trading.map((item, index) => (
                      <div key={item.id}>
                        <ColorItemComponent 
                          item={item} 
                          index={index} 
                          total={colorsByCategory.trading.length} 
                        />
                        {index < colorsByCategory.trading.length - 1 && <Separator />}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}